import { Toaster } from 'react-hot-toast';
import SupportEmail from '../../components/supportEmail/SupportEmail';
import useIsMobile from '../../hooks/useIsMobile';

function PrivacyPolicyPage() {
  const { isMobile } = useIsMobile();

  return (
    <div
      className={`px-4 md:px-24 mx-auto py-10 leading-6 ${
        isMobile ? 'px-5' : ''
      }`}
    >
      <section>
        <Toaster />
        {!isMobile && (
          <h1 className='text-[1.25rem] leading-[2rem] md:text-[40px] text-[#2D2D2D] md:leading-[40px] mb-8 font-bold'>
            Program Privacy Policy
          </h1>
        )}
        <p className='mb-6'>
          <strong>Effective Date: October 21, 2026</strong>
        </p>
        <p>
          Your privacy is important to us and we want you to feel comfortable
          using the Savers App Inc. Program (<strong>"Savers App"</strong>,{' '}
          <strong>"we"</strong>, <strong>"us"</strong> or <strong>"our"</strong>{' '}
          as the context dictates) as it is described and outlined in the
          Program Terms. Given this, we have created this Privacy Policy (the{' '}
          <strong>"Privacy Policy"</strong>) which applies to any person using
          the Program and shall be read in conjunction with our Program Terms.
        </p>
        <p className='mt-4'>
          This Privacy Policy explains the data protection and usage practices
          of Savers App Inc. when you join or use the program and applications,
          to which this policy is linked, whether you access the Program through
          the Savers App application or through a partner's application or
          channel that offers the Program (<strong>"Partner Channel"</strong>).
        </p>
      </section>

      <section className='px-2 md:px-0'>
        <ol className='list-decimal mt-8 bold-marker ml-[1.2rem]'>
          <li className='mb-5'>
            <h3 className='font-bold'>GENERAL</h3>
            <p className='mt-2'>
              To provide you with the benefits of the Program, we need to
              collect information, which may also include your personal
              information or data, as defined under the applicable law. Please
              be aware that your participation and use of our program may also
              result in the collection of certain information on account of
              technologies detailed in our Privacy Policy.
            </p>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              INFORMATION WE COLLECT THROUGH THE PROGRAM
            </h3>
            <div className='mt-2'>
              <p className='mt-2'>
                Depending on how you use the Program, we may collect the
                categories below. A category may be personal information even
                when it does not identify you on its own.
              </p>
              <p className='mt-6'>
                Identifiers and account information, such as name, email
                address, telephone number, account or user identifier, login
                credentials or authentication records, IP address,
                partner-channel identifier, and records of the notices, terms
                and consents presented to you and your actions in response.
              </p>
              <p className='mt-6'>
                We provide an opportunity for you to unsubscribe or opt-out of
                contact on an ongoing basis by using the unsubscribe mechanism
                at the bottom of our e-mails, in the account settings section of
                the App, or by e-mailing <SupportEmail />.
              </p>
              <p className='mt-6'>
                <strong>Non-personal information</strong>, that does not
                directly or indirectly reveal your identity or directly relate
                to an identifiable individual, such as statistical or aggregated
                information. Statistical or aggregated data does not directly
                identify a specific person, but we may derive non-personal
                statistical or aggregated data from personal information. For
                example, we may aggregate personal information to calculate the
                percentage of people accessing a specific feature of the
                Program.
              </p>
              <p className='mt-6'>
                <strong>Technical information</strong>, including your login
                information, browser/App type and version, time zone setting,
                browser/App plug-in types and versions, device operating system
                and platform, information about your internet connection, the
                equipment you use to access the Program, and usage details. We
                use cookies and similar technologies as described in the
                "Cookies and Similar Technologies" section below.
              </p>
              <p className='mt-6'>
                <strong>Internet, device and usage information</strong>, such as
                URLs, clickstream, pages or offers viewed, searches,
                interactions, timestamps, session duration, referring and exit
                pages, errors, device and advertising identifiers, app events,
                cookie or SDK identifiers, and support interactions. We treat
                this information as personal information when it can reasonably
                be linked to you, your household, account or device.
              </p>
              <p className='mt-6'>
                <strong>Partner and enrollment information.</strong> If you
                enroll through a Partner Channel, we may receive your
                identifiers, contact details, eligibility or membership
                information, partner-channel identifier, Program Account setup
                data, time-stamped records showing the notices, terms and
                consents presented and the actions you took, and, where the
                partner supplies the account to which your Cashback is paid or
                credited, the account details needed to make that payment. We
                will identify any materially different partner-specific
                collection in the enrollment flow.
              </p>
              <p className='mt-6'>
                <strong>Transaction Data.</strong> When you participate in the
                Program by enrolling an eligible card (i.e. Visa, Mastercard)
                you will be asked to provide consent to have transactions on
                that enrolled card monitored by the respective card network and
                for that card network to share certain details of the monitored
                transactions with Savers App Inc., and with Olive Group Ltd (
                <strong>"Program Vendor"</strong>). Each party will access this
                Transaction Data from your monitored transactions. This
                Transaction Data may include the date, time, amount, merchant
                category, and merchant name. You may revoke your consent and opt
                out of transaction monitoring on your enrolled cards at any time
                by going to your settings and disabling or removing the card
                from the Program.
              </p>
              <p className='mt-6'>
                When you enroll an Eligible Card, the card details are submitted
                through the approved secure enrollment process to the supported
                payment network or its service provider. Savers App receives a
                token or other identifier representing the Enrolled Card and
                does not receive the full card number or card security code
                through this enrollment flow. Transaction details and the token
                may then be shared among Savers App, the identified Program
                service providers, and the applicable payment network as
                described here and in the Card Enrollment Consent presented when
                you enroll a card.
              </p>
              <p className='mt-6'>
                <strong>Round-Up and Donation information.</strong> If you
                enable Round-Up or Donate Cashback where your Program offers
                them, we collect your Round-Up settings (including your Monthly
                Cap and Minimum Round-Up Donation), the Round-Up Amounts
                calculated for each Round-Up Card, your monthly Round-Up charges
                and their status, and your Donation history and statements. Your
                Round-Up Card details for the monthly Round-Up charge are
                collected and stored by our payment processor, Stripe; Savers
                App receives a payment token, the card brand and the last four
                digits, not the full card number or security code.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>HOW WE USE YOUR INFORMATION</h3>
            <p className='mt-2'>
              We use information that we collect about you or that you provide
              to us, including any personal information: to present our Program
              and its contents to you; to fulfill the purposes for which you
              provided the information or that were described when it was
              collected, or any other purpose for which you provide it; to
              provide you with notices about your account/subscription,
              including expiration and renewal notices; to carry out our
              obligations and enforce our rights arising from any contracts with
              you, including for billing and collection or to comply with legal
              requirements; to improve our Program, products or services,
              marketing, or customer relationships and experiences; in any other
              way we may describe when you provide the information; or for any
              other purpose with your consent.
            </p>
            <div className='mt-4'>
              <p>
                <strong>How we use your Transaction Data.</strong> When you
                participate in our Program by enrolling an eligible credit card
                (i.e. Visa, Mastercard), you will be required to provide consent
                to have transactions on your enrolled card monitored by the card
                network. That card network will share with us, and with the
                Program Vendor, the Transaction Data. Notwithstanding anything
                to the contrary in our Program Terms and our Privacy Policy, we
                and the Program Vendor will use your Transaction Data solely to:
                enable card-linked offers; identify offers that may be of
                interest to you based on location; provide program
                notifications; calculate the Cashback Reward payment amounts
                payable to you under the Program or, if Donate Cashback is on,
                donated on your behalf; if you have enabled Round-Up on a Card,
                calculate Round-Up Amounts on all transactions reported for that
                Card and operate the monthly Round-Up charge described in the
                Program Terms; confirm Qualifying Transactions with the Merchant
                with whom the transaction occurred, sharing details of a
                transaction only with the Merchant that was a party to that
                transaction, for reward validation and funding purposes; and
                report in aggregate to merchants the success of their offers in
                the Program.
              </p>
            </div>
            <div className='mt-4'>
              <p>
                <strong>Other information and purposes.</strong> We may also
                collect commercial and reward history; payout details (such as
                the bank account information you provide for payouts, or the
                token of the Enrolled Card you designate as a Payout Card),
                payout status and related financial information; Round-Up
                settings, charges and Donation history; customer-support,
                referral or content submissions; approximate or precise location
                if enabled; marketing and communication preferences; fraud,
                security and compliance indicators; and inferences used to
                personalize the Program. We use these categories to operate
                accounts and rewards, operate Round-Up and Donations,
                authenticate users, process payouts, communicate and support
                members, detect and prevent fraud, comply with law, measure and
                improve the Program, personalize offers, and conduct marketing
                only under the choices described below.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              PERMISSION FOR COLLECTION OF INFORMATION
            </h3>
            <div className='mt-2 space-y-4'>
              <p>
                <strong>LEGAL BASES AND CHOICES.</strong> We collect, use and
                disclose personal information as reasonably necessary to provide
                the Program you request, perform our contract with you, comply
                with law, protect the Program and prevent fraud, and pursue
                other legitimate or permitted business purposes. Where
                applicable law requires consent, including for specified
                sensitive information, non-essential technologies, precise
                location or marketing, we will request that consent separately
                and explain how to withdraw it. This Privacy Policy describes
                our practices; merely viewing it is not blanket consent to every
                practice.
              </p>
              <p>
                If you decline an optional collection or withdraw consent, we
                will explain any Program feature that cannot reasonably be
                provided without that information. Withdrawal does not affect
                processing already lawfully completed. You may manage available
                choices in Program and device settings or contact the Privacy
                Officer.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              SHARING OF INFORMATION COLLECTED FOR THE PROGRAM
            </h3>
            <div className='mt-2'>
              <p>
                We may disclose aggregated information about the use of the
                Program, and information that does not identify any individual,
                without restriction.
              </p>
              <p className='mt-4'>
                We may disclose personal information that we collect or you
                provide as described in this privacy policy:
              </p>
            </div>
            <div className='ml-8 mt-4'>
              <ol className='list-decimal'>
                <li>to our subsidiaries and affiliates;</li>
                <li>
                  to Program partners: if you enrolled in the Program through,
                  or linked the Program to, a Partner Channel, we may share with
                  that partner your enrollment status, your acceptance records
                  for the Program Terms, this Privacy Policy and related
                  consents, and reporting about your Program participation
                  reasonably necessary for the partner to operate its program
                  with us. Partners receive this information under contractual
                  obligations of confidentiality and data protection and may use
                  it only in connection with the Program and their own
                  agreements with you. We also share with the partner the
                  Cashback amounts to be paid or credited to your account with
                  the partner and the related payout reporting. Other
                  transaction-related reporting to partners is provided on an
                  aggregated basis that does not identify your individual
                  transactions;
                </li>
                <li>
                  in accordance with applicable law, to a buyer or other
                  successor in the event of a merger, divestiture,
                  restructuring, reorganization, dissolution, or other sale or
                  transfer of some or all of the Company's assets, whether as a
                  going concern or as part of bankruptcy, liquidation, or
                  similar proceeding, in which personal information held by us
                  or the Program Vendor about our customers and users is among
                  the assets transferred;
                </li>
                <li>
                  to contractors, service providers, and other third parties we
                  use to support our business (such as analytics and search
                  engine providers that assist us with the improvement and
                  optimization of the App) and who are contractually obligated
                  to keep personal information confidential, use it only for the
                  purposes for which we disclose it to them, and to process the
                  personal information with the same standards set out in this
                  policy;
                </li>
                <li>
                  to fulfill the purpose for which you provide it. For example,
                  if you give us an email address to use the "refer a friend"
                  feature of the App, we will transmit the contents of that
                  email and your email address to the recipients;
                </li>
                <li>
                  for any other purpose disclosed by us when you provide the
                  information;
                </li>
                <li>with your consent;</li>
                <li>
                  to the Charity: in Programs that include Round-Up or Donate
                  Cashback, we share with the Charity your name, email address
                  and the dates and amounts of your Donations so that it can
                  issue donation receipts and acknowledge your Donations, if
                  applicable. The Charity uses this information under its own
                  privacy policy.
                </li>
              </ol>
            </div>
            <div className='mt-4'>
              <p>
                We may also disclose your personal information: to comply with
                any court order, law, or legal process, including to respond to
                any government or regulatory request, in accordance with
                applicable law; to enforce or apply the Program Terms and other
                agreements, including for billing and collection purposes;
                and/or if we believe disclosure is necessary or appropriate to
                protect the rights, property, or safety of us, our customers, or
                others. This includes exchanging information with other
                companies and organizations for the purposes of fraud protection
                and credit risk reduction.
              </p>
              <p className='mt-4'>
                When you participate in the Program your Transaction Data and a
                token representing your enrolled card will be shared between us,
                the Program Vendor, and the card network associated with your
                enrolled card (i.e. Visa, Mastercard). No additional Personal
                Information is shared with these third parties about your
                account with us without your consent.
              </p>
              <p className='mt-4'>
                <strong>SALE, SHARING AND TARGETED ADVERTISING.</strong> We do
                not sell personal information for money, and we do not share
                personal information for cross-context behavioral advertising or
                process personal information for targeted advertising. Our
                service providers (including our attribution and messaging
                providers) may process personal information only to provide
                services to us under contractual restrictions.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>COOKIES AND SIMILAR TECHNOLOGIES</h3>
            <div className='mt-2 space-y-4'>
              <p>
                We and our service providers use cookies, software development
                kits (SDKs), pixels, and similar technologies to operate and
                improve the Program. These fall into the following categories:
                Strictly necessary (required to provide the Program, such as
                keeping you logged in and maintaining security; these cannot be
                switched off); Preferences (remember your settings and choices);
                Analytics (help us understand how the Program is used so we can
                improve it); and Advertising (used to present offers and
                promotions that may interest you, including location-based
                offers where you have enabled location services).
              </p>
              <p>
                You can control app permissions through device settings and
                available Program settings. Where required, we will present a
                consent tool before activating non-essential cookies, SDKs or
                similar technologies. We will honor legally recognized universal
                opt-out signals for sale, sharing or targeted advertising. The
                tool will identify the technologies, providers, purposes and
                durations that apply to the relevant site or app. Withdrawing
                consent will be as easy as granting it and will not disable
                strictly necessary functions.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              CROSS-BORDER PROCESSING AND SECURITY
            </h3>
            <p className='mt-2'>
              Savers App and its service providers process personal information
              in the United States, where courts, law-enforcement or regulators
              may have lawful access. Before transferring information outside
              Quebec or another jurisdiction that requires an assessment, we
              assess relevant privacy factors and use contractual and security
              safeguards. We maintain administrative, technical and physical
              safeguards designed for the nature of the information, but no
              system is completely secure. If a breach affects you, we will
              provide notice where law requires it.
            </p>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>DATA RETENTION</h3>
            <div className='mt-2 space-y-4'>
              <p>
                We retain each category of personal information only for as long
                as reasonably necessary and proportionate for the disclosed
                purpose. Our retention schedule currently provides: account and
                acceptance records - the life of your Program Account plus five
                (5) years; card tokens and enrollment records - deleted within
                thirty (30) days after unenrollment; payment tokens for the
                monthly Round-Up charge - deleted within thirty (30) days after
                Round-Up is turned off on that Card or the Card is unenrolled;
                transaction and reward records - seven (7) years; payment,
                Donation and tax records - seven (7) years; support and dispute
                records - five (5) years; security logs - two (2) years;
                marketing preference and suppression records - for as long as
                needed to honor your preferences; and backups - a rotation of up
                to ninety (90) days. We then delete, de-identify or aggregate
                the information, unless a documented legal hold or statutory
                obligation requires longer retention. We apply deletion to
                service providers and production systems and allow backup copies
                to age out under the disclosed rotation while restricting their
                use.
              </p>
              <p>
                <strong>Account deletion.</strong> When you delete your Program
                Account, we delete the personal information in it that this
                schedule and applicable law do not require us to keep, including
                your profile details, device and location information,
                preferences, and marketing and analytics data, and we instruct
                our service providers to do the same. The Program Terms provide
                for a closing period after deletion, normally ninety (90) days
                and never more than one hundred and eighty (180) days, during
                which your remaining Cashback Rewards are settled. During that
                period we keep only an identifier for your Program Account
                (including, if you enrolled through a Partner Channel, the
                identifier assigned to you by that partner), your payout method,
                one contact channel for settlement notices, and your Cashback
                Reward records. If any Cashback Reward remains unpaid after the
                closing period, we also keep your contact channel and payout
                method until it is paid or transferred to the state under
                unclaimed-property law. After that, the remaining records are
                retained only under the periods stated above.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>YOUR US STATE PRIVACY RIGHTS</h3>
            <div className='mt-2 space-y-4'>
              <p>
                Depending on your state of residence, you may have rights to
                access and know about personal information; delete it; correct
                inaccuracies; receive a portable copy; opt out of sale, sharing,
                targeted advertising, or qualifying profiling; limit certain
                uses or disclosures of sensitive personal information; withdraw
                consent; appeal a denied request; and receive equal service and
                price without unlawful retaliation. These rights are subject to
                legal exceptions. We honor legally recognized universal opt-out
                signals for the browser or device sending the signal. We will
                not require identity verification for an opt-out request unless
                reasonably necessary to prevent fraud and permitted by law.
              </p>
              <p>
                To exercise these rights, contact us at <SupportEmail /> with
                the subject line "Privacy Rights Request", or use the in-app
                request option where available. We will verify your identity
                using the email address and phone number associated with your
                Program Account, and may request additional information where
                necessary. You may use an authorized agent to submit a request
                on your behalf; we will require proof of the agent's authority
                and verification of your identity. If we deny your request, you
                may appeal by replying to our decision; we will respond to
                appeals within the period required by your state's law.
              </p>
            </div>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>YOUR CANADIAN PRIVACY RIGHTS</h3>
            <p className='mt-2'>
              If you are in Canada, we handle personal information under PIPEDA
              and applicable provincial private-sector privacy laws, including
              the laws of Alberta, British Columbia and Quebec where applicable.
              Subject to legal limits, you may request access and correction,
              withdraw consent, challenge compliance, and receive information
              about our policies, service providers and cross-border handling.
              Quebec residents may also have rights concerning automated
              decisions, cessation of dissemination or de-indexing, and
              computerized portability. We will provide the French version and
              French rights-request channel required for Quebec. You may
              complain to the Office of the Privacy Commissioner of Canada or
              the applicable provincial regulator, including Quebec's Commission
              d'acces a l'information.
            </p>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>
              PERSONAL INFORMATION FROM USERS BELOW 18 YEARS OF AGE
            </h3>
            <p className='mt-2'>
              We do not knowingly collect personal information from users below
              18 years of age. If you believe that such a user has provided
              personal information to us, please contact us via <SupportEmail />,
              so that we can identify and delete that user's personal
              information.
            </p>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>PRIVACY CONTACT</h3>
            <p className='mt-2'>
              Questions, requests and complaints may be directed to: Privacy
              Officer, Savers App Inc., 1111B S Governors Ave, Unit 21598,
              Dover, DE 19904; email: <SupportEmail />, subject line 'Privacy.'
              The person accountable for our privacy program is our Chief
              Operating Officer, who serves as Privacy Officer. We will maintain
              procedures to receive, authenticate, track, respond to and appeal
              requests in each supported language.
            </p>
          </li>

          <li className='mb-5'>
            <h3 className='font-bold'>CHANGES TO PRIVACY POLICY</h3>
            <p className='mt-2'>
              This Policy is current as of the effective date above. We may
              update it prospectively to reflect changes in our practices or
              law. We will post each updated version and retain prior versions.
              If a change is material, we will provide direct notice before it
              takes effect where reasonably practicable. We will obtain new
              consent before a materially different practice where applicable
              law requires consent; prior notice or continued use will not
              substitute for that consent.
            </p>
          </li>
        </ol>
      </section>
    </div>
  );
}

export default PrivacyPolicyPage;
